// Shared billboard table — column widths, typography and colors are the single
// source of truth for BillboardView (kiosk) and the public series schedule page.

export const BILLBOARD_FONT = 'Arial, sans-serif';

export const formatBillboardDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

export const BillboardDivider = () => (
  <div style={{ height: 2, background: 'linear-gradient(to right, transparent, #b91c1c, #d4af37, #b91c1c, transparent)', flexShrink: 0, zIndex: 1 }} />
);

export const BillboardHeading = ({ date, isLandscape = true }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: isLandscape ? '14px 40px 8px' : '12px 24px 6px', flexShrink: 0, zIndex: 1 }}>
    <div data-testid="billboard-heading" style={{ fontSize: 40, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#fff', fontFamily: BILLBOARD_FONT, textAlign: 'center' }}>Daily Tournament Schedule</div>
    <div data-testid="billboard-date" style={{ fontSize: 30, color: '#facc15', fontFamily: BILLBOARD_FONT, textAlign: 'center', marginTop: 4 }}>{formatBillboardDate(date)}</div>
  </div>
);

const tdStyle = {
  padding: '4px 8px',
  textAlign: 'center',
  verticalAlign: 'middle',
  fontFamily: BILLBOARD_FONT,
  fontSize: 17,
  fontWeight: 500,
};

const COLUMNS = [
  { label: 'TIME', w: '7%', align: 'center' },
  { label: '#', w: '4%', align: 'center' },
  { label: 'EVENT', w: '30%', align: 'left' },
  { label: 'BUY-IN', w: '16%', align: 'left' },
  { label: 'STACK', w: '8%', align: 'center' },
  { label: 'DURATION', w: '8%', align: 'center' },
  { label: 'REG CLOSE', w: '8%', align: 'center' },
  { label: 'ENTRIES', w: '10%', align: 'center' },
];

// "YYYY-MM-DD HH:MM" -> "HH:MM" or "HH:MM +Nd" relative to the event date.
export function formatCloseSubtext(t) {
  if (!t || !t.reg_close_time) return null;
  const [datePart, timePart] = t.reg_close_time.split(' ');
  if (!timePart) return null;
  if (t.date && datePart && datePart !== t.date) {
    const start = new Date(t.date + 'T00:00:00');
    const close = new Date(datePart + 'T00:00:00');
    const diff = Math.round((close - start) / 86400000);
    if (diff > 0) return `${timePart} +${diff}d`;
    if (diff < 0) return `${timePart} ${diff}d`;
  }
  return timePart;
}

const EntriesCell = ({ t }) => {
  const players = t.total_players || 0;
  const entries = t.total_entries || t.total_entries_day || 0;
  const isFinished = t.reg_close === 'FINISHED';
  const hasWinner = t.winner_name || t.winner_prize;
  if (isFinished && hasWinner) {
    return (
      <div style={{ lineHeight: 1.15 }}>
        <div style={{ color: '#facc15', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', fontSize: 13 }}>{'\u{1F3C6}'} {t.winner_name}</div>
        {t.winner_prize && <div style={{ color: '#ffffff', fontSize: 12, fontWeight: 600, marginTop: 1 }}>{t.winner_prize}</div>}
      </div>
    );
  }
  if (isFinished) {
    return <div style={{ color: '#ffffff', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Final — {entries} entries</div>;
  }
  if (players === 0 && entries === 0) return <span style={{ color: 'rgba(255,255,255,0.15)' }}>-</span>;
  return <>{players}<span style={{ margin: '0 4px', fontWeight: 600 }}>/</span>{entries}</>;
};

export const BillboardTable = ({ tournaments, rowH, padding = '0 40px 16px', fill = true, emptyText = '' }) => (
  <div style={{ flex: fill ? 1 : 'none', overflow: 'hidden', padding, zIndex: 1 }} data-testid="billboard-table">
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <thead>
        <tr style={{ background: 'rgba(14,7,7,0.8)' }}>
          {COLUMNS.map((col) => (
            <th key={col.label} style={{
              padding: '8px 6px', fontSize: 15, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: '#facc15', textAlign: col.align,
              width: col.w, fontFamily: BILLBOARD_FONT,
            }}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {tournaments.length === 0 && emptyText && (
          <tr><td colSpan={COLUMNS.length} style={{ ...tdStyle, padding: '40px 8px', color: 'rgba(255,255,255,0.45)', background: 'rgba(10,5,5,0.7)' }} data-testid="billboard-empty">{emptyText}</td></tr>
        )}
        {tournaments.map((t, idx) => {
          const h = typeof rowH === 'function' ? rowH(tournaments.length) : rowH;
          const isPremium = t.is_premium;
          const bg = isPremium ? 'rgba(25,20,8,0.75)' : idx % 2 === 0 ? 'rgba(10,5,5,0.7)' : 'rgba(14,7,7,0.7)';
          return (
            <tr key={t.id} data-testid={`billboard-row-${t.id}`} style={{ background: bg, borderBottom: '1px solid rgba(255,255,255,0.04)', borderLeft: isPremium ? '3px solid #d4af37' : '3px solid transparent' }}>
              <td style={{ ...tdStyle, height: h, fontWeight: 700, color: '#facc15' }}>{t.time}</td>
              <td style={{ ...tdStyle, height: h, color: '#ffffff', fontWeight: 600 }}>{t.event_number}</td>
              <td style={{ ...tdStyle, height: h, textAlign: 'left', paddingLeft: 12 }}>
                <div style={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.2 }}>{t.event_name}</div>
              </td>
              <td style={{ ...tdStyle, height: h, textAlign: 'left', paddingLeft: 12, color: '#facc15' }}>
                {t.buyin ? (
                  <div>
                    <div style={{ fontWeight: 700 }}>{t.buyin}</div>
                    {t.buyin_breakdown && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>({t.buyin_breakdown})</div>}
                    {t.bounty && <div style={{ fontSize: 13, color: '#facc15', fontWeight: 600 }}>({t.bounty})</div>}
                  </div>
                ) : '-'}
              </td>
              <td style={{ ...tdStyle, height: h, color: '#ffffff' }}>{t.stack || '-'}</td>
              <td style={{ ...tdStyle, height: h, color: '#ffffff' }}>{t.duration || '-'}</td>
              <td style={{ ...tdStyle, height: h, color: '#ffffff', fontWeight: ['CLOSED', 'FINISHED', 'RUNNING'].includes(t.reg_close) ? 700 : 400 }}>
                {t.reg_close || '-'}
                {t.reg_close === 'RUNNING' && formatCloseSubtext(t) && (
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#facc15', marginTop: 2, letterSpacing: '0.02em' }}>closes {formatCloseSubtext(t)}</div>
                )}
              </td>
              <td style={{ ...tdStyle, height: h, fontWeight: 800, fontSize: 22, color: '#facc15' }}><EntriesCell t={t} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export const BillboardFooter = ({ padding = '8px 40px' }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding, flexShrink: 0, zIndex: 1 }}>
    <span style={{ fontSize: 10, color: 'rgba(212,175,55,0.3)', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.2em' }}>#rdptplus</span>
    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', fontFamily: "'Manrope', sans-serif" }}>playreddragon.com</span>
    <span style={{ fontSize: 10, color: 'rgba(212,175,55,0.3)', fontFamily: "'JetBrains Mono', monospace" }}>RDPT PLUS+ 2026</span>
  </div>
);
